// สมุดสัตว์ (Animal-dex): สะสมชนิดที่เคยเกิดเป็น (docs/PLAN.md เฟส 3)
// เก็บในเบราว์เซอร์ของผู้เล่นเท่านั้น (src/client/storage.ts) ส่วนนี้เป็นตรรกะล้วนๆ ทดสอบได้โดยไม่ต้องมีเบราว์เซอร์

import type { IucnCode } from "../data/iucn";
import { summarize } from "../engine/life";
import type { DeathCause, LifeState } from "../engine/types";

export interface DexEntry {
  id: number;
  sci: string;
  nameTh: string | null;
  groupId: string;
  subgroupId: string;
  iucn: IucnCode | null;
  firstSeen: string; // วันที่ YYYY-MM-DD ที่เกิดเป็นชนิดนี้ครั้งแรก
  lastSeen: string;
  lives: number; // จำนวนชีวิตที่เล่นจบ
  adults: number; // จำนวนชีวิตที่รอดจนโตเต็มวัย
  bestAgeDays: number;
  bestOffspring: number;
  totalOffspring: number;
  maxGeneration: number; // สายตระกูลยาวที่สุด
  farthestKm: number;
  causes: Partial<Record<DeathCause, number>>;
}

export interface Dex {
  version: 1;
  entries: Record<string, DexEntry>; // key = species id
}

export const emptyDex = (): Dex => ({ version: 1, entries: {} });

const day = (now: Date) => now.toISOString().slice(0, 10);

function blank(life: LifeState, now: Date): DexEntry {
  const s = life.species;
  return {
    id: s.id,
    sci: s.sci,
    nameTh: s.nameTh,
    groupId: s.groupId,
    subgroupId: s.subgroupId,
    iucn: s.iucn,
    firstSeen: day(now),
    lastSeen: day(now),
    lives: 0,
    adults: 0,
    bestAgeDays: 0,
    bestOffspring: 0,
    totalOffspring: 0,
    maxGeneration: 1,
    farthestKm: 0,
    causes: {},
  };
}

/** ตอนเริ่มชีวิต: ใส่ชนิดนี้ลงสมุด (ยังไม่นับเป็นชีวิตที่เล่นจบ) คืนค่า isNew ถ้าเพิ่งเจอครั้งแรก */
export function recordBirth(dex: Dex, life: LifeState, now = new Date()): { dex: Dex; isNew: boolean } {
  const key = String(life.species.id);
  const old = dex.entries[key];
  const entry = old ? { ...old, lastSeen: day(now) } : blank(life, now);
  return { dex: { ...dex, entries: { ...dex.entries, [key]: entry } }, isNew: !old };
}

/** ตอนจบชีวิต: เก็บสถิติที่ดีที่สุดของชนิดนี้ */
export function recordLife(dex: Dex, life: LifeState, now = new Date()): Dex {
  if (life.alive) return dex;
  const key = String(life.species.id);
  const e = dex.entries[key] ?? blank(life, now);
  const s = summarize(life);
  const cause = s.cause;
  const entry: DexEntry = {
    ...e,
    nameTh: life.species.nameTh ?? e.nameTh,
    iucn: life.species.iucn ?? e.iucn,
    lastSeen: day(now),
    lives: e.lives + 1,
    adults: e.adults + (s.reachedAdult ? 1 : 0),
    bestAgeDays: Math.max(e.bestAgeDays, Math.round(s.ageDays)),
    bestOffspring: Math.max(e.bestOffspring, s.offspring),
    totalOffspring: e.totalOffspring + s.offspring,
    maxGeneration: Math.max(e.maxGeneration, life.lineage?.generation ?? 1),
    farthestKm: Math.max(e.farthestKm, Math.round(life.geo?.km ?? 0)),
    causes: cause ? { ...e.causes, [cause]: (e.causes[cause] ?? 0) + 1 } : e.causes,
  };
  return { ...dex, entries: { ...dex.entries, [key]: entry } };
}

/** อ่านข้อมูลที่เก็บไว้ ถ้าผิดรูปแบบก็เริ่มสมุดใหม่ (ไม่ให้เกมพัง) */
export function parseDex(raw: string | null): Dex {
  if (!raw) return emptyDex();
  try {
    const data = JSON.parse(raw) as Partial<Dex>;
    if (data.version !== 1 || typeof data.entries !== "object" || data.entries === null) return emptyDex();
    const entries: Record<string, DexEntry> = {};
    for (const [k, e] of Object.entries(data.entries)) {
      if (e && typeof e.id === "number" && typeof e.sci === "string" && typeof e.subgroupId === "string") entries[k] = e;
    }
    return { version: 1, entries };
  } catch {
    return emptyDex();
  }
}

export const dexList = (dex: Dex) => Object.values(dex.entries);
